import { Lock, LogIn } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * SignInLockedSection — wraps a homepage section and hides it behind sign-in.
 *
 * When the user is signed in, children render unchanged.
 * When signed out, a styled "Sign in to unlock" CTA renders in place,
 * preserving the section's anchor + visual rhythm on the homepage.
 *
 * Dispatches a global `omni:open-auth` event that Header listens for to
 * open the existing AuthModal.
 */
export default function SignInLockedSection({
  anchor,
  testId,
  tag,
  title,
  highlight,
  highlightColor = "#D4AF37",
  description,
  bg = "bg-[#080808]",
  icon = "🔒",
  children,
}) {
  const { user } = useAuth();
  if (user) return children;

  const openAuth = () => {
    window.dispatchEvent(new CustomEvent("omni:open-auth"));
  };

  return (
    <section
      id={anchor}
      data-testid={testId || `locked-${anchor}`}
      className={`py-24 md:py-32 ${bg} relative`}
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] mb-5">
          <Lock size={14} />
          <span className="font-accent text-xs tracking-[0.3em] uppercase">
            {tag || "Members Only"}
          </span>
        </div>
        <h2 className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-4">
          {title}
          {highlight && (
            <>
              {" "}
              <span style={{ color: highlightColor }}>{highlight}</span>
            </>
          )}
        </h2>
        {description && (
          <p className="text-neutral-400 max-w-xl mx-auto mb-8 text-sm md:text-base">
            {description}
          </p>
        )}
        <div
          className="relative max-w-md mx-auto bg-[#0F0F0F] border-2 border-zinc-800 p-8 hover:border-[#D4AF37]/50 transition-colors"
          style={{
            backgroundImage:
              "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.08), transparent 60%)",
          }}
        >
          <div className="text-5xl mb-4 select-none" aria-hidden>
            {icon}
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
            Sign In Required
          </div>
          <p className="text-neutral-300 text-sm mb-6">
            Create a free OmniMuscle account to unlock{" "}
            <span className="text-[#D4AF37] font-bold">{title}{highlight ? ` ${highlight}` : ""}</span>{" "}
            and start earning Phoenix Tokens 🐦‍🔥
          </p>
          <button
            data-testid={`unlock-cta-${anchor}`}
            onClick={openAuth}
            className="btn-omni btn-red w-full justify-center"
          >
            <LogIn size={14} /> Sign In to Unlock
          </button>
          <div className="text-[10px] uppercase tracking-widest text-neutral-600 mt-4">
            Free · 30-second sign up · No credit card
          </div>
        </div>
      </div>
    </section>
  );
}
